import { publishElementsDataType } from '../../Pages/Flo/Flo.types';
import React, {
	CSSProperties,
	useCallback,
	useEffect,
	useState,
	useLayoutEffect,
} from 'react';
import { get, min } from 'lodash';
import styles from './TextElement.module.css';
import classNames from 'classnames';
import { IconPlayerPauseFilled, IconPlayerPlayFilled } from '@tabler/icons-react';
import Processing from '../../Pages/Flo/Components/Processing/Processing';
import { calculateAspectRatioFit } from '../../utils/video.utils';
import { scaleLinear } from 'd3-scale';
import { Button, Tooltip } from '@mui/material';
import { updateDialogAction } from '../VideoDialog/VideoDialog.reducer';
import { useDispatch } from 'react-redux';
import { setAutoPlay } from '../../Pages/Flo/Flo.actions';
import DynamicFontLoader from './TextFontLoader';
import { calculateScaleValue } from '../../utils/video.utils';
import { element } from 'prop-types';

const fontFactor = 600;

export const TextElement = ({
	currentElement,
	currentElementIndex,
	goToStep,
	playerDim,
	width: vWidth,
	height: vHeight,
	hasNotPlayed,
	setCountDownPause,
	isDocument,
	elementVoiceResources,
	togglePause,
	floId,
	floIndex,
}: {
	setCountDownPause?(value: boolean): void;
	togglePause?(value: boolean): void;
	currentElementIndex?: number;
	currentElement?: publishElementsDataType | null;
	goToStep?(currentElementId?: string, next?: boolean, arrowClick?: boolean): void;
	playerDim: {
		width: number;
		height: number;
	};
	width: number;
	height: number;
	hasNotPlayed?: boolean;
	isWidthLessThan650?: boolean;
	isDocument?: boolean;
	elementVoiceResources?:
		| { [key: string]: { voiceId: string; scriptId: string } }
		| undefined;
	floId: string;
	floIndex: number;
}) => {
	const calculateNewFontSize = useCallback(
		(originalFontSize: number, width: number, height: number): number => {
			// Calculate scaling factors for width and height
			const widthScale = vWidth / width;
			const heightScale = vHeight / height;

			// Use the average of the scaling factors to maintain proportionality
			const averageScale = (widthScale + heightScale) / 2;

			// Calculate the new font size
			const newFontSize = originalFontSize * averageScale;

			return newFontSize;
		},
		[vWidth, vHeight]
	);

	const calCropFontSize = useCallback(
		(
			originalFontSize: number,
			fontCropWidth: number,
			fontCropHeight: number,
			fontEleWidth: number,
			fontEleHeight: number
		): number => {
			const scaleX = vWidth / fontCropWidth;
			const scaleY = vHeight / fontCropHeight;

			// 2. Calculate new parent dimensions
			const newWidth = fontEleWidth * scaleX;
			const newHeight = fontEleHeight * scaleY;

			// Calculate scaling factors for width and height
			const widthScale = newWidth / fontEleWidth;
			const heightScale = newHeight / fontEleHeight;

			// Use the average of the scaling factors to maintain proportionality
			const averageScale = (widthScale + heightScale) / 2;

			// Calculate the new font size
			const newFontSize = originalFontSize * averageScale;

			return newFontSize;
		},
		[vWidth, vHeight]
	);

	const dispatch = useDispatch();
	const [textDimension, setTextDimension] = useState<CSSProperties>({});
	const textRef = React.useRef<HTMLDivElement | null>(null);

	const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
	const [countDown, setCountDown] = useState(-1);
	const [pauseCountDown, setPauseCountDown] = useState(true);
	const { addFont } = DynamicFontLoader();

	const txtStyle = {
		fontFamily: get(currentElement, 'fontName') || 'Roboto', // Default to Arial if fontFamily is not provided
		color: get(currentElement, 'fontColor') || 'black', // Default to black if color is not provided
	};

	console.log('txtStyle', txtStyle);
	console.log('currentElement', currentElement);

	const voiceProperties = get(
		elementVoiceResources,
		`${get(currentElement, 'voiceConfig.scriptId')}_${get(
			currentElement,
			'voiceConfig.voiceId'
		)}`
	);

	const voiceDuration = Math.ceil(get(voiceProperties, 'durationInMillis', 0) / 1000);
	const { textCountdown, duration } =
		voiceDuration > get(currentElement, 'duration', 1)
			? {
					textCountdown: get(currentElement, 'duration', 1) - (voiceDuration - countDown),
					duration: voiceDuration,
			  }
			: { textCountdown: countDown, duration: get(currentElement, 'duration', 1) };

	const loadNextStep = useCallback(() => {
		// goToStep(undefined, true);
		setCountDown((i) => i - 1);
		if (get(currentElement, 'editorElementName') !== 'text') {
			if (timer) {
				clearInterval(timer);
				setTimer(null);
				setCountDown(0);
			}
		}
	}, [currentElement, timer, setTimer, setCountDown]);

	const startCountdown = useCallback(() => {
		if (
			get(currentElement, 'behaviourType') &&
			get(currentElement, 'behaviourType') !== 'stayFor'
		)
			return;

		setCountDown(duration);
		const newTimer = setInterval(loadNextStep, 1000);
		setTimer(newTimer);
		setPauseCountDown(false);
	}, [setPauseCountDown, setTimer, currentElement, setCountDown, loadNextStep, duration]);

	const onTextLoaded = useCallback(() => {
		console.log('on text loaded', currentElementIndex);
		if (timer) {
			clearInterval(timer);
		}
		if (!hasNotPlayed) {
			startCountdown();
		}
	}, [hasNotPlayed, startCountdown, timer]);

	const onTogglePauseCountDown = useCallback(
		(e: React.MouseEvent) => {
			dispatch(setAutoPlay({ data: false, floId, floIndex }));
			e.stopPropagation();
			if (
				get(currentElement, 'behaviourType') &&
				get(currentElement, 'behaviourType') !== 'stayFor'
			)
				return;
			setPauseCountDown((i) => !i);
			if (timer) {
				clearInterval(timer);
				setTimer(null);
				togglePause?.(true);
			} else {
				const newTimer = setInterval(loadNextStep, 1000);
				setTimer(newTimer);
				togglePause?.(false);
			}
		},
		[onTextLoaded, setTimer, setPauseCountDown, timer, currentElement, togglePause]
	);

	useEffect(() => {
		if (countDown === -1 && timer) {
			console.log('clearing timer');
			clearInterval(timer);
			setCountDown(0);
			setTimer(null);
			goToStep?.(undefined, true);
		}
	}, [setTimer, setCountDown, goToStep, countDown, timer]);

	useEffect(() => {
		if (get(currentElement, 'editorElementName') === 'text') {
			const playerWidth = (get(playerDim, 'width', 0) as number) || 16;
			const playerHeight = (get(playerDim, 'height', 0) as number) || 9;
			const canvasWindowWidth = get(
				currentElement,
				'canvasRenderConfig.windowCoordinates.width',
				0
			);
			const canvasWindowHeight = get(
				currentElement,
				'canvasRenderConfig.windowCoordinates.height',
				0
			);
			const canvasCoordinates = get(
				currentElement,
				'canvasRenderConfig.canvasCoordinates'
			);

			const xScale = scaleLinear([0, canvasWindowWidth], [0, playerWidth]);
			const yScale = scaleLinear([0, canvasWindowHeight], [0, playerHeight]);

			const oldWidth = get(canvasCoordinates, 'width', 0);
			const oldHeight = get(canvasCoordinates, 'height', 0);
			const newWidth = xScale(oldWidth);
			const newHeight = yScale(oldHeight);
			const oldFontSize = Number(get(currentElement, 'fontSize'));
			const newFontSize = xScale(oldFontSize);

			let finalTextSize: {
				width: number;
				height: number;
				left: number;
				top: number;
				transform?: string;
				maxWidth?: string | number;
				maxHeight?: string | number;
				fontSize?: string;
			} = {
				width: 0,
				height: 0,
				left: 0,
				top: 0,
				transform: '',
				fontSize: '10px',
			};

			finalTextSize = {
				width: newWidth,
				height: newHeight,
				left: xScale(get(canvasCoordinates, 'x', 0)),
				top: yScale(get(canvasCoordinates, 'y', 0)),
				transform: `rotate(${get(canvasCoordinates, 'angle', 0)}deg)`,
				fontSize: `${Math.round(newFontSize)}px`,
			};
			setTextDimension({ ...finalTextSize, ...txtStyle });
		}
	}, [isDocument, playerDim.width, playerDim.height, currentElement]);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			// Remove the component from the DOM after the specified duration
			const element = textRef.current;
			if (element) {
				// element.remove();
				togglePause?.(true);
			}
		}, duration);

		// Clean up the timeout when the component unmounts
		return () => clearTimeout(timeoutId);
	}, [duration, togglePause]);

	useEffect(() => {
		return () => {
			if (timer) clearInterval(timer);
			setCountDownPause?.(true);
		};
	}, [timer, setCountDownPause]);

	useEffect(() => {
		setCountDownPause?.(!timer);
	}, [timer, setCountDownPause]);

	useEffect(() => {
		//@ts-ignore
		console.log('loading text', currentElement?.text, currentElement?.time);
		addFont(get(currentElement, 'fontName', 'Inter'));
		onTextLoaded();
	}, [currentElement]);

	return (
		<div
			ref={textRef}
			className={classNames(styles.textContainer, {
				[styles.hasNotPlayed]: hasNotPlayed,
			})}
			onClick={onTogglePauseCountDown}
		>
			<div
				key={get(currentElement, 'id')} // Added key for each element
				className={styles.sizeCalculator}
				style={{
					fontSize: `${calculateNewFontSize(
						get(currentElement, 'fontSize', 8),
						get(currentElement, 'canvasRenderConfig.windowCoordinates.width', 0),
						get(currentElement, 'canvasRenderConfig.windowCoordinates.height', 0)
					)}px`,
					color: get(currentElement, 'fontColor', 'white'),
					transform: `translate(${calculateScaleValue(
						get(currentElement, 'canvasRenderConfig.windowCoordinates.width', 0),
						playerDim.width,
						get(currentElement, 'canvasRenderConfig.canvasCoordinates.x', 0)
					)}px, ${calculateScaleValue(
						get(currentElement, 'canvasRenderConfig.windowCoordinates.height', 0),
						playerDim.height,
						get(currentElement, 'canvasRenderConfig.canvasCoordinates.y', 0)
					)}px) rotate(${get(
						currentElement,
						'canvasRenderConfig.canvasCoordinates.angle',
						0
					)}deg)`,
					//@ts-ignore
					'--calculated-font-size': `${
						get(currentElement, 'cropped')
							? calCropFontSize(
									get(currentElement, 'fontSize', 8),
									get(currentElement, 'fontCropData.fontCropWidth', 0),
									get(currentElement, 'fontCropData.fontCropHeight', 0),
									get(currentElement, 'fontCropData.fontEleWidth', 0),
									get(currentElement, 'fontCropData.fontEleHeight', 0)
							  )
							: calculateNewFontSize(
									get(currentElement, 'fontSize', 8),
									get(currentElement, 'canvasRenderConfig.windowCoordinates.width', 0),
									get(currentElement, 'canvasRenderConfig.windowCoordinates.height', 0)
							  )
					}px`,
					'--calculated-font-family': get(currentElement, 'fontName', 'Inter'),
				}}
				dangerouslySetInnerHTML={{ __html: get(currentElement, 'text', '--') }}
			></div>
			{!isDocument && (
				<>
					{(!get(currentElement, 'behaviourType') ||
						get(currentElement, 'behaviourType') === 'stayFor') &&
						(textCountdown > 0 || pauseCountDown) &&
						!hasNotPlayed && (
							<div
								className={classNames(styles.textCountDown, {
									[styles.pauseAnimation]: pauseCountDown,
								})}
								style={{
									// @ts-ignore
									'--anim-completed': textCountdown - 1,
									'--anim-duration': get(currentElement, 'duration', 1),
								}}
							>
								{pauseCountDown ? '' : textCountdown}

								<div className={styles.timer}></div>
								<div
									className={styles.toggleButtonContainer}
									data-testid={pauseCountDown ? 'play-icon' : 'pause-icon'}
								>
									{pauseCountDown ? (
										<IconPlayerPlayFilled size={16}></IconPlayerPlayFilled>
									) : (
										<IconPlayerPauseFilled size={16}></IconPlayerPauseFilled>
									)}
								</div>
							</div>
						)}
				</>
			)}
		</div>
	);
};
